/** @jsxImportSource @emotion/react */
import { useGetUserHistoricalBalances } from "../../../service/api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { useParams } from "react-router";
import { Drawer } from "../../../components/Drawer";
import { formatDateWithYearAndTime, formatMoneyAmount } from "../../../util";
import { css } from "@emotion/react";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { SpinnerBlock } from "../../common/Spinner";
import { UserBalance } from "../../../service/api-response/UserBalanceSuccessResponse";
import { DownloadAsCSVButton } from "../../../components/DownloadAsCSV";

type Props = {
  balance: UserBalance;
  isOpen: boolean;
  onClose: () => void;
};

const elementStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const infoStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const amountStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const HistoricalBalancesDrawer = ({
  balance,
  isOpen,
  onClose,
}: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { data, isLoading } = useGetUserHistoricalBalances(
    organizationKeyName,
    balance.countryCode,
    isOpen,
    id
  );

  const sortedData = (data || []).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Drawer
      title="Top up balance history"
      width={700}
      open={isOpen}
      onClose={onClose}
      buttons={
        <DownloadAsCSVButton
          height="auto"
          data={sortedData.map((b) => {
            const { id, amount, currency, createdAt, ...rest } = b;
            return {
              amount: formatMoneyAmount(amount, currency),
              ...rest,
              createdAt: formatDateWithYearAndTime(createdAt),
            };
          })}
          fileName="Top up balance history"
        >
          Download as CSV
        </DownloadAsCSVButton>
      }
    >
      <>
        {isLoading ? (
          <SpinnerBlock />
        ) : (
          <>
            {sortedData.map((b, i) => (
              <div css={elementStyle} key={i}>
                <div css={infoStyle}>
                  <StyledText
                    className="bodyTextSemibold mb-0"
                    color={colors.textPrimary}
                  >
                    {b.description}
                  </StyledText>
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    {formatDateWithYearAndTime(b.createdAt)}
                  </StyledText>
                </div>
                <div css={amountStyle}>
                  <StyledText
                    className="bodyTextSemibold mb-0"
                    color={colors.textPrimary}
                  >
                    {formatMoneyAmount(b.amount, balance.currency)}
                  </StyledText>
                  <StyledText
                    className="bodySubtextRegular mb-0"
                    color={colors.textSecondary}
                  >
                    {formatMoneyAmount(
                      balance.balance -
                        sortedData
                          .slice(0, i)
                          .reduce((acc, curr) => acc + curr.amount, 0),
                      balance.currency
                    )}
                  </StyledText>
                </div>
              </div>
            ))}
          </>
        )}
      </>
    </Drawer>
  );
};
